import { companyService } from "services";
import {
	buildOnboardingRestartPatch,
	buildStepCompletePatch,
} from "../config/onboardingSteps";

/**
 * Persistencia del onboarding en el company.
 *
 * Fuente de verdad:
 *  - `companies.onboarding_complete` → onboarding cerrado
 *  - `companies.onboarding_*_complete` → cada paso marcado a mano
 *
 * GET/PUT `/companies/{id}`. Sin localStorage.
 *
 * El PUT reenvía el company del GET salvo `email`: ese campo llega cifrado
 * y el CRUD lo rechaza como inválido. El resto de campos (incluido `name`
 * cifrado) sí los acepta.
 */

const LEGACY_STORAGE_PREFIX = "plain.onboarding.dismissed.";

/** Limpia restos de la caché local antigua (ya no se usa). */
const clearLegacyLocalCache = () => {
	try {
		Object.keys(localStorage)
			.filter((k) => k.startsWith(LEGACY_STORAGE_PREFIX))
			.forEach((k) => localStorage.removeItem(k));
	} catch (e) {
		// no-op
	}
};

clearLegacyLocalCache();

let cachedCompany = null;
let cachedCompanyId = null;
let persistChain = Promise.resolve();

const runSerialized = (task) => {
	const next = persistChain.then(task, task);
	persistChain = next.catch(() => undefined);
	return next;
};

/** Quita metadatos de response y el email cifrado, que no valida el PUT. */
export const toCompanyPayload = (company, patch = {}) => {
	const { ok, errors, email, ...rest } = company || {};
	return {
		...rest,
		...patch,
	};
};

const persistPatch = async (companyId, patch) => {
	if (!companyId) return false;

	return runSerialized(async () => {
		try {
			let company = cachedCompany;
			if (!company || cachedCompanyId !== companyId) {
				company = await companyService.getById(companyId);
				if (!company || company.ok === false) return false;
				cachedCompany = company;
				cachedCompanyId = companyId;
			}

			const alreadyApplied = Object.entries(patch).every(
				([key, value]) => company[key] === value
			);
			if (alreadyApplied) return true;

			const payload = toCompanyPayload(company, patch);
			const response = await companyService.update(payload);
			if (!(response && response.ok)) return false;

			cachedCompany = { ...company, ...patch };
			cachedCompanyId = companyId;
			return true;
		} catch (e) {
			return false;
		}
	});
};

export const onboardingStorage = {
	clearCache() {
		cachedCompany = null;
		cachedCompanyId = null;
	},

	/**
	 * Lee el company completo (y actualiza la caché de persistencia).
	 * @returns {Promise<object|null>}
	 */
	async fetchCompany(companyId) {
		if (!companyId) return null;

		try {
			const company = await companyService.getById(companyId);
			if (!company || company.ok === false) return null;
			cachedCompany = company;
			cachedCompanyId = companyId;
			return company;
		} catch (e) {
			return null;
		}
	},

	/**
	 * Lee `onboarding_complete` del company.
	 * @returns {Promise<boolean>}
	 */
	async fetchComplete(companyId) {
		const company = await this.fetchCompany(companyId);
		return company?.onboarding_complete === true;
	},

	/**
	 * Persiste el flag de onboarding cerrado.
	 * @returns {Promise<boolean>}
	 */
	async persistComplete(companyId, complete) {
		return persistPatch(companyId, { onboarding_complete: !!complete });
	},

	/**
	 * Reabre el onboarding y deja todos los pasos sin marcar.
	 * @returns {Promise<boolean>}
	 */
	async persistRestart(companyId) {
		return persistPatch(companyId, buildOnboardingRestartPatch());
	},

	/**
	 * Persiste el completado explícito de un paso.
	 * @returns {Promise<boolean>}
	 */
	async persistStepComplete(companyId, stepKey, complete) {
		const patch = buildStepCompletePatch(stepKey, complete);
		if (!Object.keys(patch).length) return false;
		return persistPatch(companyId, patch);
	},
};
